// src/components/app-sidebar.tsx
import * as React from "react";
import { NavMain } from "@/components/nav-main";
import { NavSecondary } from "@/components/nav-secondary";
import { NavUser } from "@/components/nav-user";
import { NavInfoCard } from "@/components/nav-info-card";
import { NavVersionCard } from "@/components/nav-version-card";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuItem,
    useSidebar,
} from "@/components/ui/sidebar";
import { Link } from "react-router-dom";
import { dashboardRoutes, supportRoutes, getSidebarItems } from "@/config/routes";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/TenantContext";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import { canAccessRootAdmin } from "@/api/root-admin/root-admin.utils";
import { ShieldCheck } from "lucide-react";

// const baseSubscriptionStyle = "text-[9px] h-3.5 px-1.5 font-medium tracking-wide rounded-sm uppercase";

// const subscriptionLabels = {
//     free: {
//         label: 'free',
//         className: cn(
//             baseSubscriptionStyle,
//             'bg-gray-100 text-gray-600 ring-1 ring-gray-200/50'
//         )
//     },
//     professional: {
//         label: 'Pro',
//         className: cn(
//             baseSubscriptionStyle,
//             'bg-gradient-to-r from-purple-50 to-blue-50',
//             'text-blue-700 ring-1 ring-blue-200'
//         )
//     }
// } as const;

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
    const { user } = useAuth();
    const { branding } = useTenant();
    const { state } = useSidebar();
    const { theme } = useTheme();

    // Determine if we should use dark logo
    const isDarkMode = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const effectiveLogoUrl = isDarkMode && branding?.dark_logo_url ? branding.dark_logo_url : branding?.logo_url;

    // const subscriptionInfo = user?.subscription_tier
    //     ? subscriptionLabels[user.subscription_tier as keyof typeof subscriptionLabels]
    //     : subscriptionLabels.free;

    const baseNavItems = getSidebarItems(dashboardRoutes);
    const secondaryNavItems = getSidebarItems(supportRoutes);

    // Add Platform Team to sidebar if user has access (super_admin or manager)
    const mainNavItems = React.useMemo(() => {
        if (user?.role && canAccessRootAdmin(user.role)) {
            return [
                ...baseNavItems,
                {
                    title: "Platform Team",
                    href: "platform-team",
                    icon: <ShieldCheck className="h-4 w-4" />,
                    description: "Manage platform developers and managers",
                }
            ];
        }
        return baseNavItems;
    }, [baseNavItems, user?.role]);

    // Render favicon/icon in collapsed state, full logo in expanded state
    const renderLogo = () => {
        const isCollapsed = state === "collapsed";

        // In collapsed state, show icon/favicon
        if (isCollapsed) {
            const iconUrl = branding?.icon_url || branding?.favicon_url;
            
            if (iconUrl) {
                return (
                    <img 
                        src={iconUrl} 
                        alt={branding?.company_name || 'Icon'} 
                        className="h-8 w-8 object-contain"
                        onError={(e) => {
                            // On error, show first letter of company name
                            const target = e.target as HTMLImageElement;
                            const parent = target.parentElement;
                            if (parent && branding?.company_name) {
                                parent.innerHTML = `<span class="text-xl font-bold">${branding.company_name[0]}</span>`;
                            }
                        }}
                    />
                );
            }
            
            // Fallback: first letter of company name
            return (
                <span className="text-xl font-bold">
                    {branding?.company_name?.[0] || 'V'}
                </span>
            );
        }

        // In expanded state, show full logo
        if (!effectiveLogoUrl) {
            // If no logo URL, render company name
            return (
                <span className="text-base font-semibold truncate">
                    {branding?.company_name || 'VoiceBot'}
                </span>
            );
        }

        return (
            <img 
                src={effectiveLogoUrl} 
                alt={branding?.company_name || 'Company Logo'} 
                className="max-w-[180px] h-8 object-contain"
                onError={(e) => {
                    // On image load error, replace with company name
                    const target = e.target as HTMLImageElement;
                    const parent = target.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span class="text-base font-semibold truncate">${branding?.company_name || 'VoiceBot'}</span>`;
                    }
                }}
            />
        );
    };

    return (
        <Sidebar collapsible="icon" variant="inset" {...props}>
            <SidebarHeader className="h-16">
                <SidebarMenu>
                    <SidebarMenuItem className={cn(
                        "w-full",
                        state === "collapsed" ? "flex justify-center" : "flex justify-start"
                    )}>
                        <Link 
                            to="/dashboard" 
                            className={cn(
                                "flex items-center h-12 rounded-md transition-colors hover:bg-accent/50 active:bg-accent",
                                state === "collapsed" ? "justify-center w-full" : "justify-start w-full pl-2"
                            )}
                        >
                            {renderLogo()}
                        </Link>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>
            <SidebarContent className="pt-2">
                <NavMain items={mainNavItems} />
                <div className="mt-auto">
                    <NavSecondary items={secondaryNavItems} />
                </div>
            </SidebarContent>
            <SidebarFooter className="px-2 pt-1 pb-1 gap-2">
                <NavInfoCard />
                <NavVersionCard />
                <NavUser />
            </SidebarFooter>
        </Sidebar>
    );
}
